/* ============================================================
   SALES REPORT · EOM EDITORIAL — downloadable PDF.
   Provides the Sales report's section catalogue + cover/contents and hands them
   to the shared editorialPdfEngine (which does the block-level pagination and
   A4 rasterisation). Pairs with sales-report-export.css.
   ============================================================ */
import {
  formatINR, num, pct, esc, pad2, buildBars, htmlItem, tableItem,
  mastheadHTML, downloadEditorialReport,
} from './editorialPdfEngine';

export { formatINR };

/* ---------- per-section content (same markup as the reference) ---------- */

function summaryFlow(d) {
  const items = [htmlItem(`
    <div class="headline"><div class="headline-grid">
      <div class="hcell"><div class="h-label">Total Sales</div><div class="h-value">${formatINR(d.summary.totalSales)}</div><div class="h-desc">All dine-in and online orders for the period.</div></div>
      <div class="hcell"><div class="h-label">Expenses</div><div class="h-value">${formatINR(d.summary.expenses)}</div><div class="h-desc">Stock-in purchases plus recorded wastage.</div></div>
      <div class="hcell"><div class="h-label">Gross Profit</div><div class="h-value">${formatINR(d.summary.grossProfit)}</div><div class="h-desc">Sales after cost of goods and wastage.</div></div>
    </div></div>`)];
  const cmp = d.salesComparison || [];
  if (cmp.length) {
    items.push(htmlItem(`<div class="chart-title">Sales Comparison Chart</div>${buildBars(cmp.map(w => ({ label: w.label, value: w.amount, display: formatINR(w.amount) })), 'max')}`));
    items.push(tableItem(
      `<tr><th>Period</th><th class="num">Total Sales</th></tr>`,
      cmp.map(w => `<tr><td class="name">${esc(w.label)}</td><td class="num">${formatINR(w.amount)}</td></tr>`),
      `<tr><td class="name">Total</td><td class="num">${formatINR(d.salesComparisonTotal)}</td></tr>`,
      'margin-top:20px'
    ));
  }
  return items;
}

function dateFlow(d) {
  const db = d.dateBreakdown || [];
  const t = db.reduce((a, r) => ({ s: a.s + (+r.sales || 0), e: a.e + (+r.expense || 0), w: a.w + (+r.wastage || 0) }), { s: 0, e: 0, w: 0 });
  return [
    tableItem(
      `<tr><th>Date</th><th class="num">Total Sales</th><th class="num">Expense</th><th class="num">Wastage</th></tr>`,
      db.map(r => `<tr><td class="name">${esc(r.date)}</td><td class="num">${formatINR(r.sales)}</td><td class="num">${formatINR(r.expense)}</td><td class="num">${formatINR(r.wastage)}</td></tr>`),
      `<tr><td class="name">TOTAL</td><td class="num">${formatINR(t.s)}</td><td class="num">${formatINR(t.e)}</td><td class="num">${formatINR(t.w)}</td></tr>`
    ),
    htmlItem(`<div class="callout"><p class="k">Reading This Table</p><p>Expense is stock-in cost booked on the day; wastage is spoilage recorded separately. Both are already reflected in the period Gross Profit.</p></div>`),
  ];
}

function outletSalesFlow(d) {
  const rows = (d.outlets || []).map((o) => `<tr><td class="name">${esc(o.outlet)}</td><td class="num">${num(o.days)}</td>`
    + `<td class="num">${formatINR(o.sales)}</td><td class="num">${formatINR(o.discount)}</td>`
    + `<td class="num">${o.gross_profit == null ? '—' : `${o.gp_estimated ? '≈ ' : ''}${formatINR(o.gross_profit)}`}</td></tr>`);
  const total = (d.outlets || []).reduce((t, o) => t + o.sales, 0);
  const disc = (d.outlets || []).reduce((t, o) => t + o.discount, 0);
  const items = [tableItem(
    '<tr><th>Outlet</th><th class="num">Days</th><th class="num">Sales</th><th class="num">Discount</th><th class="num">Gross Profit</th></tr>',
    rows,
    `<tr><td class="name">TOTAL</td><td class="num"></td><td class="num">${formatINR(total)}</td><td class="num">${formatINR(disc)}</td><td class="num"></td></tr>`,
  )];
  if ((d.outlets || []).some((o) => o.gp_estimated)) {
    items.push(htmlItem('<div class="callout"><p class="k">Gross Profit</p><p>≈ marks a figure that includes an estimated cost for Not-in-Menu revenue, at the food-cost ratio of the costed dishes.</p></div>'));
  }
  return items;
}

function topFlow(d) {
  const ti = d.topItems || [];
  return [
    htmlItem(buildBars(ti.map(i => ({ label: i.name, value: i.grossProfit, display: formatINR(i.grossProfit) })), 'max')),
    tableItem(
      `<tr><th>#</th><th>Item Name</th><th class="num">Gross Profit</th><th class="num">% Share</th></tr>`,
      ti.map((i, k) => `<tr><td class="name">${k + 1}</td><td class="name">${esc(i.name)}</td><td class="num">${formatINR(i.grossProfit)}</td><td class="num">${pct(i.share)}</td></tr>`),
      '', 'margin-top:20px'
    ),
    htmlItem(`<div class="callout"><p class="k">Reading This Chart</p><p>Bar length is each dish's gross profit; the fill pattern only separates bars and carries no value. The longest bar sets the Peak (100%) scale.</p>
      <div class="legend">
        <div class="item"><span class="sw p-hatch"></span> Diagonal</div>
        <div class="item"><span class="sw p-dots"></span> Dotted</div>
        <div class="item"><span class="sw p-grid"></span> Grid</div>
        <div class="item"><span class="sw p-thin"></span> Fine line</div>
        <div class="item"><span class="sw p-solid"></span> Solid</div>
      </div>
    </div>`),
  ];
}

function profitCogsFlow(d) {
  const pc = d.profitCogs;
  const bars = buildBars([
    { label: 'Gross Profit', value: pc.grossProfit.pct, display: pct(pc.grossProfit.pct) },
    { label: 'Manufacturing Cost', value: pc.mfgCost.pct, display: pct(pc.mfgCost.pct) },
    { label: 'Waste & Losses', value: pc.waste.pct, display: pct(pc.waste.pct) },
  ], 'hundred');
  return [
    htmlItem(bars),
    tableItem(
      `<tr><th>Component</th><th class="num">Amount</th><th class="num">% of Net Sale</th></tr>`,
      [
        `<tr><td class="name">Gross Profit</td><td class="num">${formatINR(pc.grossProfit.amount)}</td><td class="num">${pct(pc.grossProfit.pct)}</td></tr>`,
        `<tr><td class="name">Manufacturing Cost</td><td class="num">${formatINR(pc.mfgCost.amount)}</td><td class="num">${pct(pc.mfgCost.pct)}</td></tr>`,
        `<tr><td class="name">Waste &amp; Losses</td><td class="num">${formatINR(pc.waste.amount)}</td><td class="num">${pct(pc.waste.pct)}</td></tr>`,
      ],
      `<tr><td class="name">Net Sale (Total)</td><td class="num">${formatINR(pc.netSale)}</td><td class="num">100%</td></tr>`,
      'margin-top:20px'
    ),
    // Estimated-cost disclosure. A PDF is forwarded to accountants and partners who
    // never saw the setting that produced it, so the note has to be IN the document —
    // it is the only thing that travels with the numbers.
    ...(d.estimatedCogsNote
      ? [htmlItem(`<p style="margin-top:14px;font-size:9px;line-height:1.45;color:#7a5b00">${esc(d.estimatedCogsNote)}</p>`)]
      : []),
    // Exclusion disclosure (A2). Separate note, deliberately: the one above says part of
    // this cost was estimated, this one says some sales are not in the table at all. A
    // reader who conflates them will reconcile the total against a figure that was never
    // meant to include those dishes.
    ...(d.excludedCogsNote
      ? [htmlItem(`<p style="margin-top:8px;font-size:9px;line-height:1.45;color:#4b5563">${esc(d.excludedCogsNote)}</p>`)]
      : []),
  ];
}

function periodFlow(d) {
  const ps = d.periodSummary || [];
  return [tableItem(
    `<tr><th>Period Scope</th><th class="num">Sales Revenue</th><th class="num">Total Expenses</th><th class="num">Net Profit/Loss</th></tr>`,
    ps.map(r => `<tr><td class="name">${esc(r.scope)}</td><td class="num">${formatINR(r.revenue)}</td><td class="num">${formatINR(r.expenses)}</td><td class="num">${formatINR(r.net)}</td></tr>`),
    ''
  )];
}

function itemsSoldFlow(d) {
  const is = d.itemsSold || [];
  const t = is.reduce((a, i) => ({ q: a.q + (+i.qty || 0), amt: a.amt + (+i.amount || 0), o: a.o + (+i.orders || 0), c: a.c + (+i.contribution || 0) }), { q: 0, amt: 0, o: 0, c: 0 });
  return [tableItem(
    `<tr><th>Item Name</th><th class="num">Quantity Sold</th><th class="num">Total Sales Amount</th><th class="num">Orders Count</th><th class="num">Contribution %</th></tr>`,
    is.map(i => `<tr><td class="name">${esc(i.name)}</td><td class="num">${num(i.qty)}</td><td class="num">${formatINR(i.amount)}</td><td class="num">${num(i.orders)}</td><td class="num">${pct(i.contribution)}</td></tr>`),
    `<tr><td class="name">Total</td><td class="num">${num(t.q)}</td><td class="num">${formatINR(t.amt)}</td><td class="num">${num(t.o)}</td><td class="num">${pct(t.c)}</td></tr>`
  )];
}

function stockInFlow(d) {
  const si = d.stockIn || [];
  const total = si.reduce((a, r) => a + (+r.cost || 0), 0);
  return [tableItem(
    `<tr><th>Ingredient Name</th><th class="num">Quantity Added</th><th>Unit</th><th class="num">Purchase Cost</th><th>Supplier</th><th>Date</th></tr>`,
    si.map(r => `<tr><td class="name">${esc(r.name)}</td><td class="num">${esc(r.qty)}</td><td>${esc(r.unit)}</td><td class="num">${formatINR(r.cost)}</td><td>${esc(r.supplier)}</td><td>${esc(r.date)}</td></tr>`),
    `<tr><td class="name">Total</td><td class="num">&mdash;</td><td>&mdash;</td><td class="num">${formatINR(total)}</td><td>&mdash;</td><td>&mdash;</td></tr>`
  )];
}

function stockOutFlow(d) {
  const so = d.stockOut || [];
  return [tableItem(
    `<tr><th>Ingredient Name</th><th class="num">Quantity Consumed</th><th>Unit</th><th>Related Item/Recipe</th><th>Date</th></tr>`,
    so.map(r => `<tr><td class="name">${esc(r.name)}</td><td class="num">${esc(r.qty)}</td><td>${esc(r.unit)}</td><td>${esc(r.related)}</td><td>${esc(r.date)}</td></tr>`),
    ''
  )];
}

function fixedExpensesFlow(d) {
  const f = d.fixedExpenses;
  const rows = [];
  f.groups.forEach((g) => {
    rows.push(`<tr><td class="name"><strong>${esc(g.name)}</strong></td><td></td><td></td><td></td><td class="num"><strong>${g.masked ? 'Hidden' : formatINR(g.total)}</strong></td></tr>`);
    g.rows.forEach((r) => rows.push(`<tr><td class="name">${esc(r.name)}</td><td>${esc(r.behavior)}</td><td>${esc(r.status)}</td><td>${esc(r.payment)}</td><td class="num">${r.amount == null ? '&mdash;' : formatINR(r.amount)}</td></tr>`));
  });
  return [
    tableItem(
      `<tr><th>Expense</th><th>Type</th><th>Status</th><th>Payment</th><th class="num">Share of Period</th></tr>`,
      rows,
      `<tr><td class="name">Total${f.excludesSalaries ? ' (excl. staff salaries)' : ''}${f.estimated ? ' &middot; est.' : ''}</td><td>&mdash;</td><td>&mdash;</td><td>&mdash;</td><td class="num">${formatINR(f.total)}</td></tr>`
    ),
    htmlItem(`<div class="callout"><p class="k">How the Share Is Taken</p><p>Monthly costs count by day: a month's cost &divide; its days &times; the days of the period inside it, and only days up to today. Sales-linked costs use the period's own orders and sales; one-time spends count on their date. Paid ${formatINR(f.paid)} &middot; Due ${formatINR(f.due)}.${f.excludesSalaries ? ' Staff salaries are visible to the owner and admin only and are left out of every total here.' : ''}</p></div>`),
  ];
}

function nonChargeableFlow(d) {
  const nc = d.nonChargeable || [];
  const total = nc.reduce((a, r) => a + (+r.amount || 0), 0);
  return [
    tableItem(
      `<tr><th>Expense Name</th><th>Category</th><th class="num">Amount</th><th>Date</th><th>Remarks</th></tr>`,
      nc.map(r => `<tr><td class="name">${esc(r.name)}</td><td>${esc(r.category)}</td><td class="num">${formatINR(r.amount)}</td><td>${esc(r.date)}</td><td>${esc(r.remarks)}</td></tr>`),
      `<tr><td class="name">Total</td><td>&mdash;</td><td class="num">${formatINR(total)}</td><td>&mdash;</td><td>&mdash;</td></tr>`
    ),
    htmlItem(`<div class="callout"><p class="k">Closing Note</p><p>End of report. All sections above are included in this download.</p></div>`),
  ];
}

/* Section catalogue — ordered exactly like the reference. `ids` maps to the export
   modal's section ids; a section is included when any of its ids is selected AND
   it has data. */
const SECTIONS = [
  { ids: ['total-sales', 'expenses', 'gross-profit', 'sales-trend'], kicker: 'Summary', title: 'Sales Report', titleSm: false,
    lede: (d) => `Period: ${esc(d.meta.period)} &nbsp;·&nbsp; Generated: ${esc(d.meta.generated)} &nbsp;·&nbsp; ${esc(d.meta.preparedFor || '')}`,
    has: () => true, flow: summaryFlow },
  { ids: ['date-breakdown'], kicker: 'Daily Detail', title: 'Date-wise Breakdown', titleSm: true,
    lede: () => 'Day-by-day sales, expense and wastage for the selected date range.',
    has: (d) => (d.dateBreakdown || []).length > 0, flow: dateFlow },
  { ids: ['top-items'], kicker: 'Performance', title: 'Top 5 Items by Gross Profit', titleSm: true,
    lede: () => 'The five dishes contributing the most gross profit for the selected period.',
    has: (d) => (d.topItems || []).length > 0, flow: topFlow },
  { ids: ['profit-cogs'], kicker: 'Margin', title: 'Gross Profit vs. Manufacturing Cost', titleSm: true,
    lede: () => 'How each ₹100 of net sale splits across profit, manufacturing cost and losses.',
    has: (d) => !!d.profitCogs, flow: profitCogsFlow },
  { ids: ['weekly-monthly-yearly-summary'], kicker: 'Period Scope', title: 'Weekly, Monthly, Yearly Comparison Summary', titleSm: true,
    lede: () => 'Sales, expenses and net profit/loss across each period scope.',
    has: (d) => (d.periodSummary || []).length > 0, flow: periodFlow },
  { ids: ['items-sold-breakdown'], kicker: 'Menu Detail', title: 'Detailed Items Sold Breakdown', titleSm: true,
    lede: () => 'Every item sold with quantity, revenue, order count and contribution to total sales.',
    has: (d) => (d.itemsSold || []).length > 0, flow: itemsSoldFlow },
  { ids: ['outlet-sales'], kicker: 'By Outlet', title: 'Outlet-wise Sales', titleSm: true,
    lede: () => 'Sales of each outlet for the period; ordinary TSR sales are one Regular TSR row.',
    has: (d) => (d.outlets || []).some((o) => o.labelled), flow: outletSalesFlow },
  { ids: ['stock-in-details'], kicker: 'Inventory In', title: 'Stock-In Details (Ingredient Purchases)', titleSm: true,
    lede: () => 'Ingredient purchases logged during the period.',
    has: (d) => (d.stockIn || []).length > 0, flow: stockInFlow },
  { ids: ['stock-out-details'], kicker: 'Inventory Out', title: 'Stock-Out Details (Ingredient Usage)', titleSm: true,
    lede: () => 'Ingredient quantities consumed against recipes during the period.',
    has: (d) => (d.stockOut || []).length > 0, flow: stockOutFlow },
  { ids: ['fixed-expenses'], kicker: 'Overheads', title: 'Fixed Expenses', titleSm: true,
    lede: () => 'Salaries, rent, monthly bills, sales-linked costs, spread purchases, tax and interest, and one-time spends — their share of the period.',
    has: (d) => !!(d.fixedExpenses && d.fixedExpenses.groups.length), flow: fixedExpensesFlow },
  { ids: ['non-chargeable-expenses'], kicker: 'Overheads', title: 'Non-Chargeable Expenses (NC)', titleSm: true,
    lede: () => 'Overheads recorded outside recipe cost for the period.',
    has: (d) => (d.nonChargeable || []).length > 0, flow: nonChargeableFlow },
];

function coverHTML(d) {
  const m = d.meta;
  return `
  <section class="page cover">
    <div class="cover-top">
      <div class="cover-brand">${esc(m.brand)}</div>
      <div class="cover-tag">${esc(m.title)}<br>Period · ${esc(m.period)}<br>Generated · ${esc(m.generated)}</div>
    </div>
    <div class="cover-mid">
      <div class="cover-kicker">${esc(m.kicker || 'Period Performance')}</div>
      <h1 class="cover-hero">${esc(m.heroLine1 || 'Sales')}<br>${esc(m.heroLine2 || 'Report')}</h1>
      <p class="cover-sub">${esc(m.subtitle || '')}</p>
      <div class="cover-strip">
        <div class="cell"><div class="l">Total Sales</div><div class="v">${formatINR(d.summary.totalSales)}</div></div>
        <div class="cell"><div class="l">Expenses</div><div class="v">${formatINR(d.summary.expenses)}</div></div>
        <div class="cell"><div class="l">Gross Profit</div><div class="v">${formatINR(d.summary.grossProfit)}</div></div>
      </div>
    </div>
    <div class="cover-foot">
      <span>Prepared for <strong>${esc(m.preparedFor || '')}</strong></span>
      <span>${esc(m.generatedBy || 'Generated by Invexis')}</span>
    </div>
  </section>`;
}

function contentsHTML(d, active) {
  const m = d.meta;
  const rows = active.map((s, i) =>
    `<div class="idx-row"><span class="idx-num">${pad2(i + 1)}</span><span class="idx-title">${esc(s.title)}</span><span class="idx-leader"></span><span class="idx-page">${pad2(i + 3)}</span></div>`
  ).join('');
  return `
  <section class="page">
    <div class="masthead">${mastheadHTML(m)}</div>
    <p class="kicker">Contents</p>
    <h2 class="sec-title">In This Report</h2>
    <hr class="rule">
    <div class="idx">${rows}</div>
    <div class="callout"><p class="k">About These Figures</p><p>All amounts are for the selected period. Gross Profit is Total Sales less cost of goods and recorded wastage; Expenses are what was bought and wasted, not what was used, so the two do not subtract into each other. The profit-vs-cost components sum to the sales they describe.</p></div>
    <div class="pfoot"><span>${esc(m.generatedBy || 'Generated by Invexis')}</span><span>Contents</span></div>
  </section>`;
}

function renderSales(data, selectedSections, fileName, output) {
  const sel = new Set(selectedSections && selectedSections.length ? selectedSections : SECTIONS.flatMap(s => s.ids));
  const active = SECTIONS.filter(s => s.ids.some(id => sel.has(id)) && s.has(data));
  return downloadEditorialReport({
    meta: data.meta,
    coverHTML: coverHTML(data),
    contentsHTML: contentsHTML(data, active),
    sections: active,
    data,
    fileName,
    output,
  });
}

export async function downloadEditorialSalesPdf(data, selectedSections, fileName) {
  await renderSales(data, selectedSections, fileName);
}

/** The same document as a Blob — what Share hands to navigator.share. */
export async function buildEditorialSalesPdfBlob(data, selectedSections, fileName) {
  return renderSales(data, selectedSections, fileName, 'blob');
}

const salesReportEditorialPdf = { formatINR, downloadEditorialSalesPdf, buildEditorialSalesPdfBlob };
export default salesReportEditorialPdf;
